import {
  MissingRecordError,
  nowIso,
  type AnnotationRecord,
  type DocumentRecord,
  type ReadingStateRecord,
} from "@/domain";
import type { MarginaliaDatabase } from "@/storage";

import { annotationRecordToExport } from "./merge-annotations";
import {
  marginaliaExportV1Schema,
  type MarginaliaAnnotationV1,
  type MarginaliaExportV1,
} from "./schemas/v1";

export type BuildDocumentExportOptions = {
  applicationVersion: string;
  exportedAt?: string;
};

function compareStrings(left: string, right: string): number {
  if (left < right) {
    return -1;
  }
  if (left > right) {
    return 1;
  }
  return 0;
}

function firstGeometryPosition(
  annotation: MarginaliaAnnotationV1,
): { x: number; y: number } {
  const rectPositions = annotation.target.geometry.rects.map((rect) => ({
    x: rect.xMin,
    y: rect.yMax,
  }));
  const point = annotation.target.geometry.point;
  if (point !== null) {
    rectPositions.push(point);
  }

  if (rectPositions.length === 0) {
    return { x: Number.POSITIVE_INFINITY, y: Number.NEGATIVE_INFINITY };
  }

  rectPositions.sort((left, right) => right.y - left.y || left.x - right.x);
  return rectPositions[0];
}

export function compareExportAnnotations(
  left: MarginaliaAnnotationV1,
  right: MarginaliaAnnotationV1,
): number {
  const pageDifference = left.pageIndex - right.pageIndex;
  if (pageDifference !== 0) {
    return pageDifference;
  }

  const leftPosition = firstGeometryPosition(left);
  const rightPosition = firstGeometryPosition(right);
  const verticalDifference = rightPosition.y - leftPosition.y;
  if (verticalDifference !== 0) {
    return verticalDifference;
  }

  const horizontalDifference = leftPosition.x - rightPosition.x;
  if (horizontalDifference !== 0) {
    return horizontalDifference;
  }

  const creationDifference = compareStrings(left.createdAt, right.createdAt);
  if (creationDifference !== 0) {
    return creationDifference;
  }

  return compareStrings(left.id, right.id);
}

function documentMetadata(
  document: DocumentRecord,
): MarginaliaExportV1["document"] {
  return {
    sha256: document.sha256,
    pdfFingerprint: document.pdfFingerprint,
    fileName: document.fileName,
    title: document.title,
    author: document.author,
    byteLength: document.byteLength,
    pageCount: document.pageCount,
  };
}

function exportReadingState(
  readingState: ReadingStateRecord | undefined,
): MarginaliaExportV1["readingState"] {
  if (!readingState) {
    return null;
  }

  return {
    pageIndex: readingState.pageIndex,
    scrollRatio: readingState.scrollRatio,
    zoom: readingState.zoom,
  };
}

export function createDocumentExportV1(
  document: DocumentRecord,
  annotations: readonly AnnotationRecord[],
  readingState: ReadingStateRecord | undefined,
  options: BuildDocumentExportOptions,
): MarginaliaExportV1 {
  const bundle: MarginaliaExportV1 = {
    format: "marginalia-annotations",
    formatVersion: 1,
    applicationVersion: options.applicationVersion,
    exportedAt: options.exportedAt ?? nowIso(),
    document: documentMetadata(document),
    annotations: annotations
      .map(annotationRecordToExport)
      .sort(compareExportAnnotations),
    readingState: exportReadingState(readingState),
  };

  return marginaliaExportV1Schema.parse(bundle);
}

export async function buildDocumentExport(
  db: MarginaliaDatabase,
  documentId: string,
  options: BuildDocumentExportOptions,
): Promise<MarginaliaExportV1> {
  return db.transaction(
    "r",
    [db.documents, db.annotations, db.readingStates],
    async () => {
      const [document, annotations, readingState] = await Promise.all([
        db.documents.get(documentId),
        db.annotations.where("documentId").equals(documentId).toArray(),
        db.readingStates.get(documentId),
      ]);

      if (!document) {
        throw new MissingRecordError("Document", documentId);
      }

      return createDocumentExportV1(
        document,
        annotations,
        readingState,
        options,
      );
    },
  );
}

export function serializeDocumentExport(bundle: MarginaliaExportV1): string {
  const validated = marginaliaExportV1Schema.parse(bundle);
  return `${JSON.stringify(validated, null, 2)}\n`;
}
